import { useMemo, useState } from 'react';
import { format } from 'date-fns';

import useControlPanelOptions from 'utils/hooks/meteorology/useControlPanelOptions';
import useStatus from 'utils/hooks/meteorology/useStatus';

export default function useMeteorologyStatus() {
  const { status: statusData } = useStatus();
  const { getModelOptions } = useControlPanelOptions('comparador');
  const meteorologyStatusData = useMemo(() => (
    statusData.map((s) => {
      const { runtimes, members } = getModelOptions(s.modelName.toLowerCase());
      const runtime = runtimes.find((r) => r.id === parseInt(s.runtime, 10));
      const member = members.find((m) => m.id === s.member);

      return {
        id: s.id,
        modelName: s.modelName,
        forecastDate: format(s.forecastDate, 'd/M/y'),
        lastUpdate: format(s.lastUpdate, 'd/M/y HH:mm:ss a'),
        member: member?.label || '',
        runtime: runtime?.label || '',
        variable: s.variable,
        status: s.status,
        statusColor: s.statusColor,
      };
    })
  ), [statusData, getModelOptions]);

  const [model, setModel] = useState<string[]>([]);
  const [forecastDate, setForecastDate] = useState<string[]>([]);
  const [runtime, setRuntime] = useState<string[]>([]);
  const [member, setMember] = useState<string[]>([]);
  const [variable, setVariable] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);

  const clearFilters = () => {
    setModel([]);
    setForecastDate([]);
    setRuntime([]);
    setMember([]);
    setVariable([]);
    setStatus([]);
  };

  const modelOptions = useMemo(() => (
    meteorologyStatusData.map((item) => item.modelName)
      .filter((value, index, self) => self.indexOf(value) === index)
  ), [meteorologyStatusData]);
  const forecastDateOptions = useMemo(() => (
    meteorologyStatusData.map((item) => item.forecastDate)
      .filter((value, index, self) => self.indexOf(value) === index)
  ), [meteorologyStatusData]);
  const runtimeOptions = useMemo(() => (
    meteorologyStatusData.map((item) => item.runtime)
      .filter((value, index, self) => self.indexOf(value) === index)
  ), [meteorologyStatusData]);
  const memberOptions = useMemo(() => (
    meteorologyStatusData.map((item) => item.member)
      .filter((value, index, self) => self.indexOf(value) === index)
  ), [meteorologyStatusData]);
  const variableOptions = useMemo(() => (
    meteorologyStatusData.map((item) => item.variable)
      .filter((value, index, self) => self.indexOf(value) === index)
  ), [meteorologyStatusData]);
  const statusOptions = useMemo(() => (
    meteorologyStatusData.map((item) => item.status)
      .filter((value, index, self) => self.indexOf(value) === index)
  ), [meteorologyStatusData]);

  const filteredItems = useMemo(() => (
    meteorologyStatusData.filter((item) => (
      (model.length === 0 || model.includes(item.modelName))
      && (forecastDate.length === 0 || forecastDate.includes(item.forecastDate))
      && (runtime.length === 0 || runtime.includes(item.runtime))
      && (member.length === 0 || member.includes(item.member))
      && (variable.length === 0 || variable.includes(item.variable))
      && (status.length === 0 || status.includes(item.status))
    ))
  ), [model, forecastDate, runtime, member, variable, status, meteorologyStatusData]);

  return {
    model,
    forecastDate,
    runtime,
    member,
    variable,
    status,
    setModel,
    setForecastDate,
    setRuntime,
    setMember,
    setVariable,
    setStatus,
    clearFilters,
    modelOptions,
    forecastDateOptions,
    runtimeOptions,
    memberOptions,
    variableOptions,
    statusOptions,
    meteorologyStatusData,
    filteredItems,
  };
}
